import { VariableType, variableTypeFromJSON } from '@/proto/common';

/** The field of a collect response entry that says what type it holds. */
export type CollectItemType = {
  variable_type?: string | null;
};

/**
 * The types a company's own collect variable can take, named as the API stores them.
 *
 * AIPCACustomVar.TYPE_CHOICES holds the same five and the API rejects any other, so a name here
 * that the API doesn't store makes every new custom variable fail to save. `satisfies` ties each
 * one to the proto enum, so renaming a VariableType fails the build here rather than at runtime.
 */
export const CUSTOM_VARIABLE_TYPE_CHOICES = [
  'TEXT',
  'NUMBER',
  'BOOL',
  'DATE_TIME',
  'DATE_TIME_RANGE',
] as const satisfies readonly (keyof typeof VariableType)[];

/** The name of a type a custom collect variable can take. */
export type CustomVariableTypeName =
  (typeof CUSTOM_VARIABLE_TYPE_CHOICES)[number];

/**
 * The type name a value holds, or undefined when it names none we offer.
 *
 * Widening happens wherever a name arrives as text: a collect response field, or a menu key from
 * the type picker.
 */
export const toCustomVariableTypeName = (
  value: string | null | undefined
): CustomVariableTypeName | undefined =>
  CUSTOM_VARIABLE_TYPE_CHOICES.find((choice) => choice === value);

/**
 * The type to publish for a variable an agent collects, from the entry the collect picker returned.
 *
 * Falls back to STRING when the entry names no type this build knows, which means the collect
 * response and this build disagree. The variable then gets text operators and compares wrongly at
 * runtime, so the fallback is logged rather than silent.
 */
export const getCollectVariableType = (item: CollectItemType): VariableType => {
  const type = item.variable_type
    ? variableTypeFromJSON(item.variable_type)
    : VariableType.UNRECOGNIZED;
  if (type === VariableType.UNRECOGNIZED) {
    console.error(
      `Collect entry has no usable variable_type (got ${JSON.stringify(item.variable_type)}); treating it as text.`
    );
    return VariableType.STRING;
  }
  return type;
};
